<template>
  <div>
    <base-chart :chartType="chartType" :chartData="chartData" :chartConfig="chartConfig" :chartSettings="chartSettings" :chartExtend="chartExtend" :loading="loading" :emptyContent="emptyContent" :showToolbox="showToolbox" :extraToolbox="extraToolbox" />
  </div>
</template>

<script>
import * as R from 'ramda'
import { numerify } from '@/filters'
import { completionDate } from '@/utils/utils'
import i18n from '@/locales'
import { currencyUnitMap } from '@/constants/currency'

export default {
  name: 'BillBar',
  props: {
    seriesArr: {
      type: Array,
      required: true,
    },
    isHistogram: { // 默认表示 Y 轴是数据轴，竖向
      type: Boolean,
      default: false,
    },
    showToolbox: {
      type: Boolean,
      default: true,
    },
    completionDate: {
      type: Boolean,
      default: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    height: {
      type: String,
      default: '460px',
    },
    chartSettings: {
      type: Object,
      default: () => ({}),
    },
    splitLineShow: {
      type: Boolean,
      default: false,
    },
    emptyContent: {},
    currencySign: {
      type: String,
      default: currencyUnitMap.CNY.sign,
    },
    numerifyFormat: {
      type: String,
      default: '0,0.00',
    },
    showLegend: {
      type: Boolean,
      default: false,
    },
    isFormatNumber: {
      type: Boolean,
      default: true,
    },
    dateMode: {},
    showDataZoom: {
      type: Boolean,
      default: false,
    },
    extraToolbox: {
      type: Object,
    },
  },
  data () {
    return {
      chartType: this.isHistogram ? 've-histogram' : 've-bar',
      pdfIcon: require('@scope/assets/pdf.svg'),
    }
  },
  computed: {
    chartData () {
      const chartData = {
        columns: [],
        rows: [],
      }
      if (this.seriesArr.length) {
        this.seriesArr.map(item => {
          const arr = Object.keys(item)
          chartData.columns = chartData.columns.concat(arr)
        })
        chartData.columns = Array.from(new Set(chartData.columns))
        if (this.dateMode === 'month' && this.completionDate) {
          chartData.rows = completionDate(this.seriesArr, new Date(), ['value'])
        } else if (this.dateMode === 'year' && this.completionDate) {
          chartData.rows = completionDate(this.seriesArr, new Date(), ['value'], true)
        } else {
          chartData.rows = this.seriesArr
        }
      }
      return chartData
    },
    chartExtend () {
      const commonSerie = {
        barMaxWidth: '16px',
        barCategoryGap: '60%',
      }

      if (!this.isHistogram) {
        commonSerie.label = {
          show: !this.isHistogram,
          normal: {
            formatter: params => `${this.currencySign} ${numerify(params.value, this.numerifyFormat)}`,
            position: 'right',
            show: true,
            color: '#939EAB',
          },
          emphasis: {
            color: '#4DA1FF',
          },
        }
      } else {
        commonSerie.barMaxWidth = '30px'
        commonSerie.barCategoryGap = '25%'
      }

      let yAxisLabelFormatter = (value, index) => {
        if (value.length > 24) {
          const start12 = value.slice(0, 6)
          const end12 = value.slice(value.length - 6)
          return `${start12}...${end12}`
        }
        return value
      }
      if (this.chartType === 've-bar') {
        yAxisLabelFormatter = (value, index) => { return '' }
        commonSerie.label = {
          show: this.isHistogram,
          normal: {
            position: 'insideTopLeft',
            offset: [15, 0],
            distance: -15,
            show: true,
            color: '#939EAB',
            formatter: this.labelFormatter,
            rich: {
              left: {
                color: '#000000',
                align: 'left',
              },
              right: {
                color: '#4DA1FF',
                width: 45,
                verticalAlign: 'top',
                padding: [0, 10, 0, 10],
                align: 'left',
              },
            },
          }, // 顶部数值标签显示
          emphasis: { color: '#4DA1FF' },
        }
      }
      return {
        series (v) {
          if (v) {
            v = v.map(i => ({ ...i, ...commonSerie }))
            v.forEach(i => {
              if (i.name === i18n.t('bill.predict')) {
                // i.itemStyle = {
                //   normal: {
                //     color: '#eee',
                //   },
                // }
                i.z = 0
              } else {
                i.barGap = '-100%'
              }
            })
            return v
          }
          return []
        },
        xAxis: {
          boundaryGap: [0, 0.01],
          splitLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
          axisLine: {
            show: false,
          },
          axisLabel: {
            show: this.isHistogram,
          },
        },
        yAxis: {
          nameGap: 30,
          splitLine: {
            show: this.splitLineShow,
          },
          axisTick: {
            show: false,
          },
          axisLine: {
            show: false,
          },
          axisLabel: {
            formatter: yAxisLabelFormatter,
          },
        },
        toolbox: {
          showTitle: false,
        },
      }
    },
    chartConfig () {
      // let title = {}
      // if (!this.seriesArr.length) {
      //   title = {
      //     show: true,
      //     textStyle: {
      //       color: 'grey',
      //       fontSize: 20,
      //     },
      //     text: '当前月份没有数据，请尝试其他月份',
      //     left: 'left',
      //     top: '20%',
      //   }
      // }
      const config = {
        // title,
        height: this.height,
        width: '100%',
        legend: {
          show: this.showLegend,
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow',
            shadowStyle: {
              color: 'rgb(77, 161, 255)',
              opacity: 0.1,
            },
          },
          position: (point, params, dom, rect, size) => {
            dom.style.border = 'none'
            dom.style.backgroundColor = 'transparent'
            const series = params.map(line => {
              if (line.value === undefined || line.value === 0.0) {
                return ''
              }
              let i18nSeriesName = line.seriesName
              if (line.seriesName === 'res_fee') {
                i18nSeriesName = this.$t('bill.text_1')
              }
              return `<div style="color: #616161;">${line.marker} <span>${i18nSeriesName}</span>  <span>${this.currencySign} ${R.is(Number, line.value) ? (this.isFormatNumber ? numerify(line.value, this.numerifyFormat) : line.value) : line.value}</span></div>`
            }).join('')
            if (series.length === 0) {
              dom.innerHTML = ''
              return
            }
            const wrapper = `<div class="chart-tooltip-wrapper">
                          <div style="color: #5D6F80;">${params[0].name}</div>
                          <div class="lines-wrapper">${series}</div>
                        </div>`
            dom.innerHTML = wrapper
          },
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '1%',
          // top: '3%',
          containLabel: true,
        },
      }
      if (this.isHistogram) {
        config.toolbox = {
          show: this.showToolbox,
          feature: {},
          right: 20,
        }
        config.grid.top = '40px'
        // 折线和柱状图切换
        config.toolbox.feature.magicType = { type: ['line', 'bar'] }
      }
      return config
    },
  },
  methods: {
    labelFormatter (params) {
      return `{left|${params.name}}{right|${this.currencySign} ${numerify(params.value, this.numerifyFormat)}}`
    },
  },
}
</script>

<style lang="less" scoped>
::v-deep {
  .ve-bar {
    max-height: 1400px; // 30条数据的高度，超过30条数据滚动
    overflow: auto;
  }
}
</style>
